import { memo } from 'react'
import styled from 'styled-components'
import { forEach } from 'ramda'
import reactStringReplace from 'react-string-replace'
import { Box, Text } from '@machobear/design-system'
import { useTranslation } from 'react-i18next'
import { colors, fontWeights } from '@/styles'
import { getMoveInDate } from '@/pages/Listing/util'
import { show as showExternalLinkModal } from '../../external-link-modal'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 12px 20px;
  background-color: #307bde;

  ${props =>
    props.isThem &&
    `
    background-color: #e6e6e6;
  `}
`

const MessageText = styled.p`
  margin: 0px;
  font-size: 14px;
  letter-spacing: 0.2px;
  line-height: 19px;
  white-space: pre-wrap;
  color: ${colors.white};

  ${props =>
    props.isDrawerMessage &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 5; 
    line-clamp: 5; 
    -webkit-box-orient: vertical;
  `}

  ${props =>
    props.isThem &&
    `
      color: ${colors.regular};
  `}
`

const Link = styled.a`
  color: ${props => (props.isThem ? colors.black : colors.white)};
  :hover {
    opacity: 0.8;
  }
  text-decoration: underline;
  cursor: pointer;
`

const PassThroughLink = styled.a`
  color: ${props => (props.isThem ? colors.black : colors.white)};
  :hover {
    opacity: 0.8;
  }
  text-decoration: underline;
  cursor: pointer;
`

const TranslatedText = styled(MessageText)`
  font-style: italic;
  margin-top: 10px;
  border-left: 1px solid ${colors.grey80};
  padding: 6px 0 6px 12px;
`

const DisplayNameText = styled.p`
  margin: 0 0 6px;
  max-width: 360px;
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${props => (props.colour ? props.colour : colors.black)};
`

const Message = ({
  event,
  isThem,
  translatedText,
  showChatName,
  showUserName,
  isDrawerMessage,
  linkPattern,
  colour
}) => {
  const { t } = useTranslation()
  const isTranslated = !!translatedText
  const possibleLinks = event?.event_info?.message.match(linkPattern)

  let parsedMessage = event?.event_info?.message

  if (possibleLinks?.length > 0) {
    forEach(link => {
      const passThrough = link.includes('liv.rent') || link.includes('livrent.page.link')

      const hasHttp = link.startsWith('http://') || link.startsWith('https://')

      const parsedLink = hasHttp ? link : `https://${link}`

      parsedMessage = reactStringReplace(parsedMessage, link, (match, i) =>
        passThrough ? (
          <PassThroughLink
            isThem={isThem}
            key={`${event?.id}-${link}`}
            href={parsedLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link}
          </PassThroughLink>
        ) : (
          <Link
            isThem={isThem}
            key={`${event?.id}-${link}`}
            onClick={() => showExternalLinkModal({ link: parsedLink })}
          >
            {link}
          </Link>
        )
      )
    }, possibleLinks)
  }

  return (
    <Wrapper isThem={isThem}>
      {(showUserName || showChatName) && (
        <DisplayNameText colour={colour}>{event?.display_name}.</DisplayNameText>
      )}
      <MessageText isDrawerMessage={isDrawerMessage} isThem={isThem}>
        {event.is_first_message === '1' && (
          <Box style={{ lineHeight: '1.4em' }} paddingBottom="xsmall">
            <Box size="xxsmall" style={{ fontWeight: 'bold' }}>
              {t('Initial Inquiry')}
            </Box>
            {event.event_info?.move_in_date && (
              <Box size="xxsmall">
                {t('Move-in Date')}: {getMoveInDate(event.event_info?.move_in_date)}
              </Box>
            )}
          </Box>
        )}

        {parsedMessage}
      </MessageText>
      {isTranslated && (
        <TranslatedText isDrawerMessage={isDrawerMessage} isThem={isThem}>
          {`"`}
          {translatedText}
          {`"`}
        </TranslatedText>
      )}
    </Wrapper>
  )
}

export default memo(Message)
